import React, { useState, useEffect } from 'react';
import { X, Undo2, CheckCircle, AlertCircle, Info, AlertTriangle } from 'lucide-react';

interface ToastProps {
  id: string;
  type?: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message?: string;
  action?: {
    label: string;
    onClick: () => void;
  };
  duration?: number;
  onClose: (id: string) => void;
}

export const Toast: React.FC<ToastProps> = ({
  id,
  type = 'info',
  title,
  message,
  action,
  duration = 5000,
  onClose,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);

  useEffect(() => {
    // Trigger entrance animation
    const showTimer = setTimeout(() => setIsVisible(true), 10);
    
    // Auto-close after duration
    const closeTimer = setTimeout(() => handleClose(), duration);

    return () => {
      clearTimeout(showTimer);
      clearTimeout(closeTimer);
    };
  }, [duration]);

  const handleClose = () => {
    setIsRemoving(true);
    setTimeout(() => onClose(id), 300); // Match animation duration
  };

  const getIcon = () => {
    switch (type) {
      case 'success':
        return <CheckCircle className="h-5 w-5 text-emerald-500" />;
      case 'error':
        return <AlertCircle className="h-5 w-5 text-red-500" />;
      case 'warning':
        return <AlertTriangle className="h-5 w-5 text-amber-500" />;
      case 'info':
      default:
        return <Info className="h-5 w-5 text-blue-500" />;
    }
  };

  const getColors = () => {
    switch (type) {
      case 'success':
        return 'border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20';
      case 'error':
        return 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20';
      case 'warning':
        return 'border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20';
      case 'info':
      default:
        return 'border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20';
    }
  };

  return (
    <div
      className={`relative flex items-center justify-between p-4 rounded-lg border shadow-lg 
        backdrop-blur-sm transition-all duration-300 ease-out max-w-md
        ${getColors()}
        ${isVisible && !isRemoving 
          ? 'transform translate-x-0 opacity-100' 
          : 'transform translate-x-full opacity-0'
        }`}
    >
      <div className="flex items-start space-x-3 flex-1">
        {getIcon()}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 dark:text-dark-text">
            {title}
          </p>
          {message && (
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {message}
            </p>
          )}
        </div>
      </div>

      <div className="flex items-center space-x-2 ml-4">
        {action && (
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              // Call the action immediately
              action.onClick();
              // Close the toast immediately for snappier feel
              handleClose();
            }}
            className="flex items-center px-3 py-1.5 text-sm font-medium text-blue-600 dark:text-blue-400 
              hover:text-blue-700 dark:hover:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/30 
              rounded transition-all duration-200 active:scale-95"
          >
            <Undo2 className="h-4 w-4 mr-1" />
            {action.label}
          </button>
        )}
        
        <button
          onClick={handleClose}
          className="p-1 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 
            hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors duration-200"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

// Toast Container Component
interface ToastContainerProps {
  toasts: Array<{
    id: string;
    type?: 'success' | 'error' | 'warning' | 'info';
    title: string;
    message?: string;
    action?: {
      label: string;
      onClick: () => void;
    };
    duration?: number;
  }>;
  onRemoveToast: (id: string) => void;
}

export const ToastContainer: React.FC<ToastContainerProps> = ({ toasts, onRemoveToast }) => {
  if (toasts.length === 0) return null;

  return (
    <div className="fixed top-4 right-4 z-[100] space-y-3">
      {toasts.map((toast) => (
        <Toast
          key={toast.id}
          id={toast.id}
          type={toast.type}
          title={toast.title}
          message={toast.message}
          action={toast.action}
          duration={toast.duration}
          onClose={onRemoveToast}
        />
      ))}
    </div>
  );
};